import React, { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Reusable 30-minute slot grid for a clinician on a date.
 * Booked slots are greyed out and cannot be selected.
 */
export default function SlotPreview({ userId, userIds, title = "Available slots", onSelect, selected, date: dateProp, onDateChange }) {
  const [dateInner, setDateInner] = useState(todayStr());
  const date = dateProp !== undefined && dateProp !== "" ? dateProp : dateInner;
  const setDate = onDateChange || setDateInner;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const ids = (userIds && userIds.length ? userIds : (userId ? [userId] : [])).filter(Boolean);
  const idsKey = ids.join(",");

  const load = useCallback(() => {
    if (!idsKey || !date) return;
    const list = idsKey.split(",");
    setLoading(true);
    const req = list.length > 1
      ? api.get("/availability/common-slots", { params: { user_ids: idsKey, date } })
      : api.get(`/availability/${list[0]}/slots`, { params: { date } });
    req.then((r) => setData(r.data))
      .catch(() => setData({ slots: [] }))
      .finally(() => setLoading(false));
  }, [idsKey, date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("availability-saved", h);
    return () => window.removeEventListener("availability-saved", h);
  }, [load]);

  const slots = data?.slots || [];

  return (
    <div className="hah-card p-6" data-testid="slot-preview">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-[#1F2937]">{title}</h3>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="slot-date" />
      </div>
      {ids.length > 1 && (
        <div className="mb-3 text-xs font-medium text-[#0B7E8C] bg-[#0B7E8C]/5 border border-[#0B7E8C]/20 rounded px-2.5 py-1.5" data-testid="slot-common-note">
          Showing slots common to all {ids.length} selected providers (booked if any one is busy).
        </div>
      )}
      <div className="flex items-center gap-4 mb-3 text-xs text-[#6B7280]">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-[#0B7E8C]" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#E5E7EB]" /> Booked</span>
      </div>
      {loading ? (
        <div className="text-sm text-[#6B7280]">Loading…</div>
      ) : slots.length === 0 ? (
        <div className="text-sm text-[#6B7280] py-6 text-center" data-testid="slot-empty">{ids.length > 1 ? "No common availability for these providers on this day." : "No availability on this day."}</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slot-grid">
          {slots.map((s) => {
            const isSel = selected === s.start;
            return (
              <button
                key={s.start}
                disabled={s.booked}
                onClick={() => !s.booked && onSelect && onSelect(s, date)}
                data-testid={`slot-${s.start}${s.booked ? "-booked" : ""}`}
                className={`px-2 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  s.booked
                    ? "bg-[#E5E7EB] text-[#9CA3AF] border-[#E5E7EB] cursor-not-allowed line-through"
                    : isSel
                      ? "bg-[#0B7E8C] text-white border-[#0B7E8C]"
                      : "bg-white text-[#0B7E8C] border-[#0B7E8C]/40 hover:border-[#0B7E8C] hover:bg-[#0B7E8C]/5"
                }`}
              >
                {s.start}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
